import React from "react";

import { addDays, addMonths, isSameDay, isWeekend } from "date-fns";
import _ from "lodash";
import { type DeepPartial } from "react-hook-form";
import * as z from "zod/v4";

import { ReactLazyLoading } from "@/containers/ReactLoading";

export const validationSchema = z.object({
  id: z.string().trim().nullish(),
  contactType: z.string().trim(),
  type: z.enum(["CONTACT", "QUESTIONNAIRE"]),
  questionnaireType: z.string(),
  contactId: z.string(),
  description: z.string().trim(),
  statusId: z.string().trim().nonempty("Bitte geben Sie einen Kontakt-Status an!"),
  nextFollowup: z.date(),

  attrs: z.object({
    callResult: z.enum(["talked", "not reached", "interview refused", "wrong data", "already contacted by sug"]),

    dataCorrect: z.boolean().optional(),
    dataCollation: z
      .object({
        company: z.string().trim().optional(),
        firstname: z.string().trim().optional(),
        lastname: z.string().trim().optional(),
        address: z.string().trim().optional(),
        zip: z.string().trim().optional(),
        city: z.string().trim().optional(),
        phone: z.string().trim().optional(),
        mobile: z.string().trim().optional(),
        email: z.string().trim().optional(),
        birthdate: z.string().trim().or(z.date()).optional(),
        hobby: z.string().trim().optional(),
        pets: z.string().trim().optional(),
        married: z.string().trim().optional(),
        children: z.string().trim().optional(),
      })
      .optional(),

    contactPerson: z
      .object({
        added: z.boolean().optional(),
        changed: z.boolean().optional(),
      })
      .optional(),

    vehicleDataCollation: z
      .object({
        changed: z.boolean().optional(),
        deletion: z.boolean().optional(),
        deletions: z
          .array(
            z.object({
              plate: z.string().trim(),
              deletionNumber: z.string().trim(),
            }),
          )
          .optional(),
        addition: z.boolean().optional(),
        additions: z
          .array(
            z.object({
              plate: z.string().trim(),
              model: z.string().trim(),
              yearOfConstruction: z.string().trim(),
              mileage: z.string().trim(),
            }),
          )
          .optional(),
      })
      .optional(),

    satisfaction: z
      .object({
        satisfied: z.boolean().optional(),
        plates: z.array(z.string().trim()).optional(),
        reason: z.string().trim().optional(),
      })
      .optional(),

    acquisition: z
      .object({
        planned: z.enum(["<6", "<12", ">12", "not planned", "no new car planned"]).optional(),
        vendor: z.enum(["sug", "other"]).optional(),
        contactAllowed: z.boolean().optional(),
        newCarType: z.enum(["new", "used", "demonstrator", "one year car", "unknown"]).optional(),
        carClass: z.enum(["car", "van", "truck"]).optional(),
        carModel: z.string().trim().optional(),
        financing: z.enum(["direct", "funding", "leasing", "unknown"]).optional(),
        budget: z.number().optional(),
        equipment: z.string().trim().optional(),
        testDriveWanted: z.boolean().optional(),
        noCarReason: z.enum(["age", "dissatisfied car", "dissatisfied service", "price", "misc"]).optional(),
      })
      .optional(),

    workshopSupport: z
      .object({
        vendor: z.enum(["sug", "other"]).optional(),
        satisfied: z.boolean().optional(),
        dissatisfiedReason: z.enum(["price", "long wait", "repair service", "misc"]).optional(),
        dissatisfiedReasonMisc: z.string().trim().optional(),
      })
      .optional(),

    caseOfNeed: z
      .object({
        case: z
          .enum([
            "service appointment",
            "reclamation",
            "dead",
            "company closed",
            "new division",
            "deletion car",
            "deletion van",
            "new address in future",
            "dont contact",
            "misc",
            "",
          ])
          .optional(),
        miscReason: z.string().trim().optional(),

        servicePlate: z.string().trim().optional(),
        serviceReason: z.string().trim().optional(),

        reclamationCar: z.string().trim().optional(),
        reclamationWorkshop: z.string().trim().optional(),
        reclamationDescription: z.string().trim().optional(),
      })
      .optional(),

    electromobility: z
      .object({
        usesElectricCars: z.boolean().optional(),
        interested: z.boolean().optional(),
        carType: z.enum(["car", "van"]).optional(),
        experience: z.boolean().optional(),
        noElectricCarReason: z.enum(["price", "range", "charge times", "infrastructure", "misc"]).optional(),
        noElectricCarReasonMisc: z.string().trim().optional(),
        nextCarElectric: z.boolean().optional(),
      })
      .optional(),

    cli: z
      .object({
        contactFrequency: z.enum(["regularly", "unregularly", "never"]).optional(),
        meApp: z.boolean().optional(),
        customerInformed: z.boolean().optional(),
      })
      .optional(),

    crmService: z
      .object({
        repeatingCall: z.boolean().optional(),
        noticedThrough: z
          .enum(["already customer", "accident", "recommendation", "service warranty", "misc"])
          .optional(),
        wouldRecomment: z.boolean().optional(),
        notRecommendingReason: z.string().trim().optional(),
        wouldBuy: z.boolean().optional(),

        privateSeller: z.boolean().optional(),
        sellerType: z.enum(["private seller", "mercedes seller", "free seller", "depending on offer"]).optional(),
        mercedesSeller: z.string().trim().optional(),
      })
      .optional(),

    overflowPhone: z
      .object({
        carsRegularlyReplaced: z.boolean().optional(),

        replaceIntervalKm: z.number().optional(),
        replaceIntervalKmText: z.string().trim().optional(),

        replaceIntervalYears: z.number().optional(),
        replaceIntervalYearsText: z.string().trim().optional(),
      })
      .optional(),

    mbvsEditing: z.enum(["rescheduling", "deletion", "decommissioning"]).optional(),

    "gespraechsnotizen-mbvs": z.string().trim().optional(),
  }),
});

export type ValuesType = z.infer<typeof validationSchema>;
export type AttrsType = ValuesType["attrs"];

export type SubcomponentPropsType = {
  contact: ContactFull;
};

export const SPECIFIC_COMPONENTS: TTDict<string, TTDict<string, React.ComponentType<SubcomponentPropsType>>> = {
  "sug-ag": {
    "cli-loyalitaetsbetreuung": ReactLazyLoading(() => import("./projects/CLILoyalitaetsbetreuung")),
    "cli-junge-sterne-ag": ReactLazyLoading(() => import("./projects/CLILoyalitaetsbetreuung")),

    "crm-sparte": ReactLazyLoading(() => import("./projects/CRMService")),
    "trapo-crm-service": ReactLazyLoading(() => import("./projects/CRMService")),

    "ueberlauftelefonie-b-kunden-transporter": ReactLazyLoading(() => import("./projects/Ueberlauftelefonie")),
  },

  "sug-gmbh": {
    "cli-junge-sterne": ReactLazyLoading(() => import("./projects/CLILoyalitaetsbetreuung")),
    "cli-loyalitaetsbetreuung-gmbh-113": ReactLazyLoading(() => import("./projects/CLILoyalitaetsbetreuung")),

    "ueberlauftelefonie-b-kunden-transporter-gmbh": ReactLazyLoading(() => import("./projects/Ueberlauftelefonie")),
  },
};

export const isHotlead = (values: DeepPartial<ValuesType>): boolean => {
  if (values.attrs?.vehicleDataCollation?.deletion || values.attrs?.vehicleDataCollation?.addition) {
    return true;
  }

  if (values.attrs?.satisfaction?.satisfied === false) {
    return true;
  }

  if (["<6", "<12"].includes(values.attrs?.acquisition?.planned || "")) {
    return true;
  }

  if (values.attrs?.caseOfNeed?.case) {
    return true;
  }

  return false;
};

const isFeastday = (date: Date, feastdays: Date[]): boolean => {
  return !!feastdays.find((feastday) => isSameDay(date, feastday));
};

export const getNextFollowup = (feastdays: Date[]) => {
  let date = addMonths(new Date(), 11);

  while (isWeekend(date) || isFeastday(date, feastdays)) {
    date = addDays(date, 1);
  }

  return date;
};

export const recalculateMbvsResult = (attrs: AttrsType) => {
  if (attrs.acquisition?.planned === ">12") {
    return "036,037";
  }

  if (attrs.acquisition?.planned === "not planned") {
    if (["deletion", "decommissioning"].includes(attrs.mbvsEditing || "")) {
      return "062";
    } else {
      return "036,037";
    }
  }

  if (["<6", "<12"].includes(attrs.acquisition?.planned || "") && attrs.acquisition?.newCarType === "new") {
    return "036,038";
  }

  if (
    ["<6", "<12"].includes(attrs.acquisition?.planned || "") &&
    ["used", "demonstrator", "one year car", "unknown"].includes(attrs.acquisition?.newCarType || "")
  ) {
    return "036,039";
  }

  if (attrs.acquisition?.planned === "no new car planned") {
    if (attrs.mbvsEditing === "rescheduling") {
      return "036,037";
    } else {
      return "062";
    }
  }

  if (attrs.caseOfNeed?.case === "reclamation") {
    return "036,045";
  }

  if (attrs.mbvsEditing === "rescheduling") {
    return "036,037";
  } else if (["deletion", "decommissioning"].includes(attrs.mbvsEditing || "")) {
    return "062";
  }

  return "";
};

export const sugNextContactValidation = (
  contact: ContactFull,
  lastCallContact: Nilable<ContactRefType>,
  _questionnaire: Nullable<CCTaskType>,
  _events: EventDetailWoContact[],
): [boolean, string] => {
  if (contact.id === lastCallContact?.id) {
    return [true, ""];
  }

  const allowedStates = [
    "Dublette",
    "Dublette (identischer Lead)",
    "Keine DSE-Telefon-Freigabe",
    "Kein DSE/Kundenstamm (MBVS)",
    "Bereits durch SuG kontaktiert",
  ];

  if (contact.status && allowedStates.includes(contact.status.name)) {
    return [true, ""];
  }

  const toolTipText =
    "Bitte rufen Sie diesen Kontakt erst an oder setzen Sie ihn auf eine der folgenden Statusmöglichkeiten: „Dublette (identischer Lead)“, „Keine DSE-Telefon-Freigabe“, „Kein DSE/Kundenstamm (MBVS)“, „Bereits durch SuG kontaktiert“";

  return [false, toolTipText];
};
